import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Space, Button, Input, Select, Typography, Alert, Switch, message } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { contractorService } from '../../services/contractor.service';
import { Contractor } from '../../types/contractor.types';

// Phase 4 - US2: Contractors List (T079)

export const ContractorsPage: React.FC = () => {
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadContractors();
  }, [search, activeFilter, page]);

  const loadContractors = async () => {
    try {
      setLoading(true);
      const result = await contractorService.listContractors({
        search,
        active: activeFilter,
        page,
        limit: 20,
      });
      setContractors(result.data);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar contratantes');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      await contractorService.updateContractor(id, { active: !currentStatus });
      message.success(`Contratante ${!currentStatus ? 'ativado' : 'desativado'}`);
      loadContractors();
    } catch (err: any) {
      message.error('Erro ao atualizar status: ' + err.message);
    }
  };

  const columns = [
    {
      title: 'Nome Fantasia',
      dataIndex: 'tradeName',
      key: 'tradeName',
      render: (_: string, record: Contractor) => (
        <Link to={`/admin/contractors/${record.id}`}>{record.tradeName || record.legalName}</Link>
      ),
    },
    {
      title: 'Razão Social',
      dataIndex: 'legalName',
      key: 'legalName',
    },
    {
      title: 'CNPJ',
      dataIndex: 'cnpj',
      key: 'cnpj',
    },
    {
      title: 'Cidade',
      key: 'city',
      render: (_: any, record: Contractor) => {
        if (!record.city) return 'N/A';
        return `${record.city.name} - ${record.city.state?.code || ''}`;
      },
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean, record: Contractor) => (
        <Switch
          checked={active}
          onChange={() => handleToggleActive(record.id, active)}
          checkedChildren="Ativo"
          unCheckedChildren="Inativo"
        />
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          Contratantes
        </Typography.Title>
        <Link to="/admin/contractors/new">
          <Button type="primary" icon={<PlusOutlined />}>Novo Contratante</Button>
        </Link>
      </Space>

      <Space style={{ marginBottom: 16 }}>
        <Input
          placeholder="Buscar por nome ou CNPJ..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <Select
          value={activeFilter?.toString() || 'all'}
          onChange={(value) =>
            setActiveFilter(value === 'all' ? undefined : value === 'true')
          }
          style={{ width: 150 }}
        >
          <Select.Option value="all">Todos</Select.Option>
          <Select.Option value="true">Ativos</Select.Option>
          <Select.Option value="false">Inativos</Select.Option>
        </Select>
      </Space>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Table
        rowKey="id"
        loading={loading}
        dataSource={contractors}
        columns={columns}
        pagination={{
          current: page,
          pageSize: 20,
          total,
          onChange: (p) => setPage(p),
        }}
      />
    </Space>
  );
};
